import {
    BUDGET_OPTIONS,
    EVENT_OPTIONS,
    GUEST_COUNT_OPTIONS,
    HEARD_FROM_OPTIONS,
    SERVICE_OPTIONS
} from '@/constants/inquiry'

// Shared by the consultation form and the inquiries API route, so the visitor
// sees exactly the rules the server enforces.

export type InquiryInput = {
    name: string
    partnerName: string
    email: string
    phone: string
    weddingDate: string
    datesFlexible: boolean
    location: string
    guestCount: string
    budget: string
    service: string
    events: Array<string>
    message: string
    heardFrom: string
}

export type InquiryField = keyof InquiryInput

export type InquiryErrors = Partial<Record<InquiryField, string>>

export const INQUIRY_LIMITS = {
    name: 120,
    partnerName: 120,
    email: 254,
    phone: 30,
    location: 160,
    message: 3000
}

// The order fields appear in the form, used to focus the first problem.
export const INQUIRY_FIELD_ORDER: Array<InquiryField> = [
    'name',
    'partnerName',
    'email',
    'phone',
    'weddingDate',
    'location',
    'guestCount',
    'budget',
    'service',
    'events',
    'message',
    'heardFrom'
]

export const EMPTY_INQUIRY: InquiryInput = {
    name: '',
    partnerName: '',
    email: '',
    phone: '',
    weddingDate: '',
    datesFlexible: false,
    location: '',
    guestCount: '',
    budget: '',
    service: '',
    events: [],
    message: '',
    heardFrom: ''
}

const EMAIL_PATTERN = /^[^\s@<>()[\],;:"]+@[^\s@<>()[\],;:"]+\.[^\s@<>()[\],;:"]{2,}$/
const PHONE_PATTERN = /^\+?[\d\s().-]{7,20}$/
const DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/

const singleLine = (value: unknown): string => typeof value === 'string' ? value.replace(/\s+/g, ' ').trim() : ''

const multiline = (value: unknown): string => typeof value === 'string' ? value.replace(/\r\n?/g, '\n').trim() : ''

export const isValidDate = (value: string): boolean => {
    if (!DATE_PATTERN.test(value)) return false

    const [year, month, day] = value.split('-').map(Number)
    const date = new Date(Date.UTC(year, month - 1, day))

    return date.getUTCFullYear() === year && date.getUTCMonth() === month - 1 && date.getUTCDate() === day
}

export const normalizeInquiry = (raw: unknown): InquiryInput => {
    const source = raw && typeof raw === 'object' ? raw as Record<string, unknown> : {}
    const events = Array.isArray(source.events) ? source.events : []

    return {
        name: singleLine(source.name),
        partnerName: singleLine(source.partnerName),
        email: singleLine(source.email).toLowerCase(),
        phone: singleLine(source.phone),
        weddingDate: singleLine(source.weddingDate),
        datesFlexible: source.datesFlexible === true,
        location: singleLine(source.location),
        guestCount: singleLine(source.guestCount),
        budget: singleLine(source.budget),
        service: singleLine(source.service),
        events: EVENT_OPTIONS.filter(option => events.includes(option)),
        message: multiline(source.message),
        heardFrom: singleLine(source.heardFrom)
    }
}

// earliestDate is a YYYY-MM-DD string: the earliest wedding date accepted.
export const validateInquiry = (input: InquiryInput, earliestDate: string): InquiryErrors => {
    const errors: InquiryErrors = {}

    if (!input.name) errors.name = "Please tell us your name."
    else if (input.name.length > INQUIRY_LIMITS.name) errors.name = `Please keep your name under ${INQUIRY_LIMITS.name} characters.`

    if (input.partnerName.length > INQUIRY_LIMITS.partnerName) errors.partnerName = `Please keep this under ${INQUIRY_LIMITS.partnerName} characters.`

    if (!input.email) errors.email = "Please add your email so we can reply."
    else if (input.email.length > INQUIRY_LIMITS.email || !EMAIL_PATTERN.test(input.email)) errors.email = "Please check your email address."

    if (input.phone && (input.phone.length > INQUIRY_LIMITS.phone || !PHONE_PATTERN.test(input.phone))) {
        errors.phone = "Please check the number, with the country code if you live outside India."
    }

    if (input.weddingDate) {
        if (!isValidDate(input.weddingDate)) errors.weddingDate = "Please choose a valid date."
        else if (input.weddingDate < earliestDate) errors.weddingDate = "Please choose a date in the future."
    }
    else if (!input.datesFlexible) {
        errors.weddingDate = "Add a date, or tick the box if your dates are not fixed yet."
    }

    if (input.location.length > INQUIRY_LIMITS.location) errors.location = `Please keep this under ${INQUIRY_LIMITS.location} characters.`

    if (!input.guestCount) errors.guestCount = "Please choose a rough guest count."
    else if (!GUEST_COUNT_OPTIONS.includes(input.guestCount)) errors.guestCount = "Please choose one of the options."

    if (input.budget && !BUDGET_OPTIONS.includes(input.budget)) errors.budget = "Please choose one of the options."

    if (input.service && !SERVICE_OPTIONS.includes(input.service)) errors.service = "Please choose one of the options."

    if (input.heardFrom && !HEARD_FROM_OPTIONS.includes(input.heardFrom)) errors.heardFrom = "Please choose one of the options."

    if (input.message.length > INQUIRY_LIMITS.message) errors.message = `Please keep your message under ${INQUIRY_LIMITS.message} characters.`

    return errors
}
